import { isAuthEntryPath } from "@/src/constants/authRoutes";
import { STORAGE_KEYS } from "@/src/constants/storageKeys";
import { propertiesService } from "@/src/services/propertiesService";
import { normalizePropertyListData } from "@/src/services/propertiesMapper";
import { fetchSubscriptionMe } from "@/src/services/subscriptionService";
import { isBrowser } from "@/src/utils/helpers";

export const POST_AUTH_DASHBOARD_PATH = "/dashboard";
/** First-time homeowners land on find-address / property setup */
export const POST_AUTH_ADD_PROPERTY_PATH = "/signup/property";
export const POST_AUTH_SUBSCRIPTION_PATH = "/subscription?required=1";

export function markPropertyOnboardingSkipped(): void {
  if (!isBrowser()) return;
  sessionStorage.setItem(STORAGE_KEYS.SKIP_PROPERTY_ONBOARDING, "1");
}

export function clearPropertyOnboardingSkipped(): void {
  if (!isBrowser()) return;
  sessionStorage.removeItem(STORAGE_KEYS.SKIP_PROPERTY_ONBOARDING);
}

export function hasSkippedPropertyOnboarding(): boolean {
  if (!isBrowser()) return false;
  return sessionStorage.getItem(STORAGE_KEYS.SKIP_PROPERTY_ONBOARDING) === "1";
}

async function userCanAddProperty(): Promise<boolean | null> {
  try {
    const me = await fetchSubscriptionMe();
    if (!me.entitlements) return null;
    return me.entitlements.canAddProperty === true;
  } catch {
    return null;
  }
}

function isSafeAppRedirect(redirectTo: string | null | undefined): redirectTo is string {
  if (!redirectTo?.startsWith("/") || redirectTo.startsWith("//")) {
    return false;
  }
  const path = redirectTo.split("?")[0] ?? redirectTo;
  return !isAuthEntryPath(path) && path !== "/verify-email" && path !== "/signup/property";
}

export async function resolvePostAuthDestination(
  redirectTo?: string | null,
): Promise<string> {
  try {
    const data = await propertiesService.list({ skipGlobalErrorHandler: true });
    const hasProperties = normalizePropertyListData(data).some((property) =>
      Boolean(property?.id),
    );
    if (!hasProperties) {
      if (hasSkippedPropertyOnboarding()) {
        return POST_AUTH_DASHBOARD_PATH;
      }
      const canAddProperty = await userCanAddProperty();
      if (canAddProperty === false) {
        return POST_AUTH_SUBSCRIPTION_PATH;
      }
      return POST_AUTH_ADD_PROPERTY_PATH;
    }
    clearPropertyOnboardingSkipped();
    if (isSafeAppRedirect(redirectTo)) {
      return redirectTo;
    }
    return POST_AUTH_DASHBOARD_PATH;
  } catch {
    // Prefer property setup over dashboard when list fails (common for brand-new accounts).
    if (hasSkippedPropertyOnboarding()) {
      return POST_AUTH_DASHBOARD_PATH;
    }
    return POST_AUTH_ADD_PROPERTY_PATH;
  }
}
